import { createApi } from '@reduxjs/toolkit/query/react'

import type {
  ChatArtifact,
  ChatArtifactFetchMode,
  ChatArtifactKind,
  Dashboard
} from '@atbash-labs/proxy-client'

import { getOntologyServiceBaseUrl } from '../../config'
import { ontologyBaseQuery } from '../core'
import { DASHBOARDS_TAG_TYPES, dashboardsApi } from '../dashboards/dashboards-api'
import { DOCS_HUB_TAG_TYPES, docsHubApi } from '../docs-hub/docs-hub-api'
import type { Doc } from '../docs-hub/types'
import { readChatArtifactExportError } from './export-error'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export const CHAT_ARTIFACTS_TAG_TYPES = ['ChatArtifact'] as const

export const chatArtifactsApi = createApi({
  reducerPath: 'chatArtifactsApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: CHAT_ARTIFACTS_TAG_TYPES,
  endpoints: (builder) => ({
    getChatArtifact: builder.query<ChatArtifact, string>({
      query: (id) => `${ONTOLOGY_SERVICE_URL}/chat-artifacts/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'ChatArtifact', id }, 'ChatArtifact']
    }),
    /** Saves a dashboard artifact to Dashboards or a document artifact to Documents. */
    exportChatArtifact: builder.mutation<
      Dashboard | Doc,
      {
        conversationId: string
        artifactId: string
        kind: ChatArtifactKind
        name: string
        path: string
      }
    >({
      query: ({ conversationId, artifactId, name, path }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/agent-conversations/${encodeURIComponent(conversationId)}/artifacts/${encodeURIComponent(artifactId)}/export`,
        method: 'POST',
        body: { name, path }
      }),
      invalidatesTags: (_result, _error, { artifactId }) => [
        { type: 'ChatArtifact', id: artifactId }
      ],
      async onQueryStarted({ kind }, { dispatch, queryFulfilled }) {
        const invalidateLibrary = () => {
          if (kind === 'document') {
            dispatch(docsHubApi.util.invalidateTags([...DOCS_HUB_TAG_TYPES]))
          } else {
            dispatch(dashboardsApi.util.invalidateTags([...DASHBOARDS_TAG_TYPES]))
          }
        }
        try {
          await queryFulfilled
          invalidateLibrary()
        } catch (error) {
          if (readChatArtifactExportError(error).kind === 'already_exported') invalidateLibrary()
        }
      }
    }),
    deleteChatArtifact: builder.mutation<void, { conversationId: string; artifactId: string }>({
      query: ({ conversationId, artifactId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/agent-conversations/${encodeURIComponent(conversationId)}/artifacts/${encodeURIComponent(artifactId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { artifactId }) => [
        { type: 'ChatArtifact', id: artifactId }
      ]
    })
  })
})

export const {
  useDeleteChatArtifactMutation,
  useExportChatArtifactMutation,
  useGetChatArtifactQuery
} = chatArtifactsApi

export type { ChatArtifact, ChatArtifactFetchMode, ChatArtifactKind }
